import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { FlaskConicalIcon } from "lucide-react";
import { CloneForMatchTestingDialog } from "@/components/CloneForMatchTestingDialog";
import { RematchClientDialog } from "@/components/RematchClientDialog";
import { formatDateTime } from "@/lib/format";

type Client = components["schemas"]["ClientAdminResponse"];

interface MatchTestingSectionProps {
  clientId: string;
  client: Client;
}

export function MatchTestingSection({ clientId, client }: MatchTestingSectionProps) {
  const [cloneOpen, setCloneOpen] = useState(false);
  const [rematchOpen, setRematchOpen] = useState(false);
  // Bumped after a clone so ClonesOfClientList refetches without a reload.
  const [clonesRefreshToken, setClonesRefreshToken] = useState(0);
  const isClone = Boolean(client.sourceClientId);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Clone for match testing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground text-sm">
            Copy this client's suppliers, bills, orders and deliveries onto a new client with three-way
            matching rewound to un-run, so you can re-run auto-matching against different AP settings on
            real documents. Bill lines are copied as they are - nothing is re-scanned - so matching is the
            only thing that changes.
          </p>
          <Button size="sm" onClick={() => setCloneOpen(true)}>
            <FlaskConicalIcon className="size-4" />
            Clone for match testing
          </Button>
        </CardContent>
      </Card>

      <ClonesOfClientCard clientId={clientId} refreshToken={clonesRefreshToken} />

      {/* Only a clone can be re-run: same gate as RematchClientAdminEndpoint. */}
      {isClone && (
        <Card>
          <CardHeader>
            <CardTitle>Re-run matching</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-muted-foreground text-sm">
              Drop every match on this clone and run the matcher again. Use it after changing this client's
              AP matching settings, so one clone covers as many settings as you want to try.
            </p>
            <Button size="sm" variant="outline" onClick={() => setRematchOpen(true)}>
              Re-run matching
            </Button>
          </CardContent>
        </Card>
      )}

      <CloneForMatchTestingDialog
        clientId={clientId}
        clientName={client.name}
        open={cloneOpen}
        onOpenChange={setCloneOpen}
        onCloned={() => setClonesRefreshToken((token) => token + 1)}
      />
      {isClone && (
        <RematchClientDialog clientId={clientId} open={rematchOpen} onOpenChange={setRematchOpen} />
      )}
    </div>
  );
}

// A client is cloned a handful of times, so this is almost always the whole list; "Load more" covers the rest.
const CLONES_PAGE_LIMIT = 20;

interface ClonesOfClientCardProps {
  clientId: string;
  refreshToken: number;
}

// Found via sourceClientId, which implies clones on the server, so no ShowClones flag.
function ClonesOfClientCard({ clientId, refreshToken }: ClonesOfClientCardProps) {
  const [clones, setClones] = useState<Client[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchFirstPage() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/clients", {
        params: { query: { SourceClientId: clientId, Limit: CLONES_PAGE_LIMIT } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load clones.");
      } else {
        setClones(data?.data ?? []);
        setNextCursor(data?.meta?.cursors?.next ?? null);
      }
      setLoading(false);
    }

    fetchFirstPage();
    return () => {
      cancelled = true;
    };
  }, [clientId, refreshToken]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    const { data, error: apiError } = await api.GET("/admin/clients", {
      params: { query: { SourceClientId: clientId, Limit: CLONES_PAGE_LIMIT, Cursor: nextCursor } },
    });
    setLoadingMore(false);
    if (apiError) {
      setError("Failed to load more clones.");
      return;
    }
    setClones((prev) => [...prev, ...(data?.data ?? [])]);
    setNextCursor(data?.meta?.cursors?.next ?? null);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <CardTitle>Clones of this client</CardTitle>
            <InfoHint label="Clones of this client">
              Every client created by cloning this one for match testing. Check here before cloning
              again. A clone you no longer need can be deleted from its own client page.
            </InfoHint>
          </div>
          {!loading && !error && (
            <span className="text-muted-foreground text-xs">
              {clones.length} clone{clones.length === 1 ? "" : "s"}
              {nextCursor ? "+" : ""}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner className="size-5" />
          </div>
        ) : error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : clones.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No clones yet. Clients created by cloning this one for match testing will show up here.
          </p>
        ) : (
          <div className="space-y-3">
            <ul className="divide-y rounded-md border">
              {clones.map((clone) => (
                <li
                  key={clone.id ?? clone.name}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                >
                  {clone.id ? (
                    <Link to={`/clients/${clone.id}`} className="font-medium hover:underline">
                      {clone.name}
                    </Link>
                  ) : (
                    <span className="font-medium">{clone.name}</span>
                  )}
                  <div className="flex items-center gap-2">
                    {clone.clonedAt && (
                      <span className="text-muted-foreground text-xs">
                        {formatDateTime(clone.clonedAt)}
                      </span>
                    )}
                    {clone.id && (
                      <Link to={`/clients/${clone.id}`}>
                        <Badge variant="secondary" className="font-mono text-xs">
                          {clone.id.slice(0, 8)}...
                        </Badge>
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {nextCursor && (
              <div className="flex items-center justify-between gap-2">
                <p className="text-muted-foreground text-xs">
                  More clones exist beyond what's shown here.
                </p>
                <Button size="sm" variant="outline" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore && <Spinner className="size-4" />}
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
